import React, { useMemo, useState } from "react";
import PhoneInputBase, {
  getCountryCallingCode,
  type Country,
} from "react-phone-number-input";
import ru from "react-phone-number-input/locale/ru.json";
import "react-phone-number-input/style.css";
import { cn } from "./ui/utils";
import { phoneDigits } from "../utils/phoneFormat";

type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "inputMode" | "value" | "onChange"
> & {
  /** Digits (country+national) or E.164. */
  value: string;
  /** Emits digits without +: countryCallingCode + national. */
  onChange: (digits: string) => void;
  countryCode?: string | null;
  onCountryChange?: (code: string) => void;
};

/**
 * Digits → E.164 for controlled input.
 * Bare calling code ("7") must NOT become national digit (that produced +77 / leading 7).
 */
function digitsToE164(raw: string, country: Country): string | undefined {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return undefined;

  const d = phoneDigits(trimmed);
  if (!d) return undefined;

  const cc = getCountryCallingCode(country);
  if (d === cc) return undefined;

  if (d.startsWith(cc) && d.length > cc.length) return `+${d}`;
  return `+${cc}${d}`;
}

export function PhoneInput({
  value,
  onChange,
  onCountryChange,
  className,
  disabled,
  id,
  name,
  placeholder,
  onKeyDown,
}: PhoneInputProps) {
  // Default Russia (not Kazakhstan) for shared +7.
  const [country, setCountry] = useState<Country>("RU");

  const e164 = useMemo(() => digitsToE164(value, country), [value, country]);

  return (
    <div className={cn("mvk-phone-input w-full", className)}>
      <PhoneInputBase
        international
        countryCallingCodeEditable={false}
        defaultCountry="RU"
        country={country}
        labels={ru}
        value={e164}
        disabled={disabled}
        id={id}
        name={name}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        onCountryChange={(next) => {
          if (!next) return;
          setCountry(next);
          onCountryChange?.(getCountryCallingCode(next));
        }}
        onChange={(next) => {
          if (!next) {
            onChange("");
            return;
          }
          const digits = phoneDigits(next);
          const cc = getCountryCallingCode(country);
          // Ignore country-only value so we never store "7" alone
          if (!digits || digits === cc) {
            onChange("");
            return;
          }
          onChange(digits);
        }}
        className="PhoneInput w-full rounded-xl border border-gray-200 bg-white px-2 py-1.5 text-[15px] focus-within:ring-2 focus-within:ring-indigo-500/30 focus-within:border-indigo-400"
        numberInputProps={{
          className:
            "PhoneInputInput !border-0 !outline-none !shadow-none bg-transparent text-[15px] text-gray-900 placeholder:text-gray-400 w-full",
        }}
      />
      <style>{`
        .mvk-phone-input .PhoneInputCountry {
          margin-right: 0.5rem;
          padding-right: 0.35rem;
          border-right: 1px solid #e5e7eb;
        }
        .mvk-phone-input .PhoneInputCountryIcon {
          width: 1.35rem;
          height: 1rem;
          box-shadow: none;
          border: 1px solid #e5e7eb;
          border-radius: 2px;
          overflow: hidden;
        }
        .mvk-phone-input .PhoneInputCountrySelectArrow {
          opacity: 0.45;
          margin-left: 0.25rem;
        }
      `}</style>
    </div>
  );
}
